/**
 * タスク一覧の右パネル。表に出さない列(割当種別・生成トリガー・方式・起票・完了・記録)と
 * 「完了にする」。タスク 1 件の API は無いので、一覧で読んだ行をそのまま受け取る。
 */
import { Alert, Space, Tag } from "antd";

import type { WfTask } from "../../lib/api";
import { jst } from "../../lib/format";
import { KeyValueList, PanelSection, SidePanel, Steps } from "../../ui";
import { taskModeLabel } from "./constants";
import { ResetInitialPassword } from "./ResetInitialPassword";
import { CompleteTask, RerunTask } from "./wfActions";
import { employeeLink, requestLink, taskStatusCell } from "./wfBits";
import { canResetInitialPassword } from "./wfHooks";

export function TaskPanel({ task, id, onClose }: { task: WfTask | undefined; id: number; onClose: () => void }) {
  if (!task) {
    return (
      <SidePanel title={`タスク #${id}`} onClose={onClose}>
        <Alert
          type="info"
          showIcon
          message="この一覧には無いタスク"
          description="ビューや条件を変えて開き直すか、申請の詳細から見る。"
        />
      </SidePanel>
    );
  }
  const t = task;
  return (
    <SidePanel
      title={`${t.section ? `${t.section}/` : ""}${t.name}`}
      ariaLabel={`タスク #${t.id} の詳細`}
      status={taskStatusCell(t)}
      sub={
        <Space size={6} wrap>
          <span>#{t.id}</span>
          {requestLink(t.request_id, t.request_kind)}
          {t.employee_name && <span>{employeeLink(t.employee_id, t.employee_name)}</span>}
        </Space>
      }
      fullHref={t.request_id ? `/hr/wf-requests/${t.request_id}?tab=tasks` : undefined}
      onClose={onClose}
      footer={
        t.status !== "done" ? (
          <Space>
            <CompleteTask task={t} />
            <RerunTask task={t} />
          </Space>
        ) : canResetInitialPassword(t) ? (
          <ResetInitialPassword task={t} />
        ) : undefined
      }
    >
      <PanelSection title="内容">
        <KeyValueList
          narrow
          items={[
            { label: "詳細", value: t.detail, wide: true },
            { label: "対象", value: t.target, copyable: true },
            { label: "操作", value: t.op_label },
            { label: "方式", value: taskModeLabel(t.mode), always: true },
            { label: "コネクタ", value: t.connector },
            { label: "担当", value: t.owner, always: true },
            { label: "実行予定日", value: t.scheduled_on },
          ]}
        />
      </PanelSection>
      {t.loans != null && (
        <PanelSection title={t.status === "done" ? "返却した貸与品" : "回収する貸与品"}>
          {t.loans.length === 0 ? (
            <div className="ui-cell-sub" data-testid="task-loans-panel">
              貸与品なし(この人に貸与中の品目は無い)
            </div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="task-loans-panel">
              {t.loans.map((l) => (
                <li key={l.id}>
                  <Space size={6} wrap>
                    <span>
                      {l.section ? `${l.section}/` : ""}
                      {l.item}
                    </span>
                    {l.asset_no && <span className="ui-num">{l.asset_no}</span>}
                    {l.equipment_name && <span className="ui-cell-sub">{l.equipment_name}</span>}
                    {l.ownership_label && <Tag color={l.ownership === "rental" ? "gold" : "default"}>{l.ownership_label}</Tag>}
                    <span className="ui-cell-sub">
                      {l.returned_at ? `返却 ${jst(l.returned_at)}` : l.lent_at ? `貸与 ${jst(l.lent_at)}` : ""}
                    </span>
                  </Space>
                </li>
              ))}
            </ul>
          )}
        </PanelSection>
      )}
      <PanelSection title="生まれた経緯">
        <KeyValueList
          narrow
          items={[
            {
              label: "割当種別",
              value: t.assignment_kind_label ? <Tag>{t.assignment_kind_label}</Tag> : "申請",
              always: true,
            },
            { label: "生成トリガー", value: t.trigger },
            { label: "外部依頼", value: t.external_request_id ? `#${t.external_request_id}` : "" , href: t.external_request_id ? `/hr/requests?panel=${t.external_request_id}` : undefined },
          ]}
        />
      </PanelSection>
      <PanelSection title="経過">
        <Steps
          items={[
            { key: "created", label: "起票", sub: jst(t.created_at), state: "done" },
            {
              key: "dispatched",
              label: "送出",
              sub: t.dispatched_at ? jst(t.dispatched_at) : t.mode === "auto" ? "コネクタ待ち" : "手作業(送出なし)",
              state: t.dispatched_at ? "done" : t.mode === "auto" && t.status !== "done" ? "current" : "todo",
            },
            {
              key: "done",
              label: t.status === "failed" ? "失敗" : "完了",
              sub: t.completed_at ? `${jst(t.completed_at)} · ${t.completed_by}` : "",
              state: t.status === "done" ? "done" : t.status === "failed" ? "rejected" : "current",
            },
          ]}
        />
        {t.result && (
          <div style={{ marginTop: 8 }}>
            <div className="ui-cell-sub">記録</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{t.result}</div>
          </div>
        )}
      </PanelSection>
    </SidePanel>
  );
}
